import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Button, DialogActions, DialogContent, Tab, Tabs, Typography } from '@mui/material'
import { selectActiveChartId } from '../../../selectors/chart'
import { AppContext } from '../../../App'
import ChartDrawingSettingsGroup from './chartDrawingSettingsGroup/chartDrawingSettingsGroup'
import { DrawingSettingGroup } from '../drawingTools/drawing.interface'
import { selectMultiPaneFullScreen } from '../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import DraggableDialogTitle from '../../common/DraggableDialogTitle'
import CustomDialog from '../../common/CustomDialog'


export type ChartDrawingSettingsModalProps = {
    id: string,
    extraSettingsGroups?: DrawingSettingGroup<any, any>[],
    onDrawingSettingGroupsUpdated?: (sgs: DrawingSettingGroup<any, any>[]) => void,
    onClose: () => void
}


const ChartDrawingSettingsModal: React.FC<ChartDrawingSettingsModalProps> = ({
                                                                                 id,
                                                                                 onClose,
                                                                                 extraSettingsGroups: esgs,
                                                                                 onDrawingSettingGroupsUpdated
                                                                             }) => {

    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)

    const [extraSettingGroups, setExtraSettingGroups] = useState<DrawingSettingGroup<any, any>[]>((esgs ?? []))
    const settingsGroups = [...appContext.getChart(activeChartId).getDrawingSettings(id), ...extraSettingGroups]
    const extraSettingGroupIds = useMemo(() => (extraSettingGroups ?? []).map((esg) => esg.id), [extraSettingGroups])
    const chart = appContext.getChart(activeChartId)
    const [selectedTab, setSelectedTab] = useState(settingsGroups.length ? settingsGroups[0].id : '')
    const container = useSelector(selectMultiPaneFullScreen)


    const tabs = useMemo(() => {
        return <Tabs
            value={selectedTab}
            onChange={(event: React.SyntheticEvent, newTab: string) => {
                setSelectedTab(newTab)
            }}
            sx={{ width: '100%', overflowX: 'auto', display: 'flex' }}
        >
            {settingsGroups.map((sG) =>
                sG.visible ? <Tab key={sG.id} label={sG.displayName} value={sG.id} sx={{textTransform: 'none'}}/> : null
            )}
        </Tabs>
    }, [settingsGroups, selectedTab])

    const updateExtraSettingGroups = useCallback((sg: DrawingSettingGroup<any, any>) => {
        const ind = extraSettingGroups.findIndex((esg) => esg.id === sg.id)
        if (ind === -1) {
            return
        }
        extraSettingGroups[ind] = { ...sg }
        setExtraSettingGroups([...extraSettingGroups])
    }, [extraSettingGroups])

    return (
        <CustomDialog
            open
            maxWidth={'sm'}
            fullWidth
            onClose={() => {
                onDrawingSettingGroupsUpdated && onDrawingSettingGroupsUpdated(extraSettingGroups)
                onClose()
            }}
            container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
        >
            <DraggableDialogTitle>
                <Typography variant="h6" component="span">Settings</Typography>
            </DraggableDialogTitle>
            <DialogContent sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    {tabs}
                </Box>
                <Box sx={{display: 'grid', '& > *': {gridArea: '1 / 1'}}}>
                    {settingsGroups.map((sG) => {
                        return <Box sx={{visibility: sG.id === selectedTab ? 'visible' : 'hidden'}} key={sG.id}>
                            <ChartDrawingSettingsGroup chart={chart} id={id} group={sG} onSettingGroupUpdated={(sg) => {
                                if (extraSettingGroupIds.includes(sg.id)) {
                                    updateExtraSettingGroups(sg)
                                }
                            }} extraSettingGroup={extraSettingGroupIds.includes(sG.id)} />
                        </Box>
                    })}
                </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
                <Button variant="contained" onClick={() => {
                    onDrawingSettingGroupsUpdated && onDrawingSettingGroupsUpdated(extraSettingGroups)
                    onClose()
                }}>Ok</Button>
            </DialogActions>
        </CustomDialog>
    )
}

export default memo(ChartDrawingSettingsModal)
